// 🎬 시댄스(ARK) 영상 모델 카탈로그 — **모델 축의 유일한 정본**.
//
// 배경(2026-08-01): 모델 분기가 전부 `isFast: boolean` 이었다. 불리언은 모델 2개까지만 표현할 수 있어서
//   3개째(Mini)를 얹는 순간 "유저가 최저가를 골랐는데 표준 모델·표준 가격이 나가는" 사고가 된다.
//   `agent-produce/core.ts` 주석이 이 위험을 이미 적어놨다 — *하위 EF 는 fast 만 fast 로 보고
//   **나머지 전부를 표준으로 취급**한다*. 그래서 불리언을 이 테이블 조회로 바꿨다.
//
// ★이 파일은 **2벌**이다: 여기(클라)와 `supabase/functions/_shared/sedeo-models.ts`(EF).
//   EF 는 src/ 를 import 할 수 없어 물리적으로 복제할 수밖에 없다(`_shared/*.ts` 는 각 EF 번들에 컴파일).
//   두 벌이 갈라지면 견적과 실제 주문이 어긋난다 — `tests/unit/sedeo-model-catalog.test.ts` 가
//   두 파일을 함께 import 해 deep-equal 로 막는다. **한쪽만 고치지 말 것.**
//
// 실측 출처: ModelArk 「Dreamina Seedance 2.0 series tutorial」 + 「Pricing」 (2026-08-01).

export interface SedeoModelDef {
  /** 앱 내부 모델 키 — UI·EF·과금이 함께 쓰는 값 */
  readonly id: string;
  /** 벤더(ARK) 모델 ID — requestBody.model 로 그대로 나간다 */
  readonly arkModel: string;
  /** 유저 노출 라벨. ★모델명 비노출 원칙(회장님) — 유저에겐 '품질'로만 보인다 */
  readonly label: string;
  readonly hint: string;
  /** 클립 길이 상한(초). ★fast 만 12 — mini 는 표준과 같은 15 다(실측) */
  readonly maxDurationSec: number;
  /** `credit_costs.model` 접두 — 실제 키는 `{billingPrefix}-{resolution}` */
  readonly billingPrefix: string;
  /** 이 모델로 주문 가능한 해상도(앱이 지원하는 집합과의 교집합) */
  readonly resolutions: readonly string[];
  /** 내부 원가 계측 배수 — 480p 표준 기준가에 곱한다(마진 분석용, 유저 과금 아님) */
  readonly costMultiplier: number;
  /** false = 카탈로그에 자리만 있고 주문 불가(화이트리스트에서 400) */
  readonly enabled: boolean;
  /** false = 벤더 API 계약 미확인. 개방되면 이 값들부터 재검증할 것 */
  readonly specsConfirmed: boolean;
  /**
   * 🙈 **노출 축**(2026-08-10 · enabled 와 직교) — true = "주문은 가능하지만 유저 선택 UI 목록에는 안 뜬다".
   *
   * 왜 `enabled` 로 못 하나: `enabled:false` 는 **주문 거절**(EF 400)이라 라우팅이 통째로 막힌다.
   * 반대로 `enabled:true` 만 켜면 품질 다이얼로그·익스퍼트 셀렉터에 새 티어가 즉시 노출돼
   * 단가 계약(credit_costs 행)·테넌트 게이트가 준비되기 전에 유저가 고를 수 있게 된다.
   * 두 질문("주문 가능한가" ↔ "고르게 보여줄 것인가")은 원래 다른 축이었고, 여태 한 플래그로
   * 뭉뚱그려져 있었다. 여기서 분리한다.
   *
   * 소비 규칙:
   *   · `isOrderableSedeoModel` / `orderableSedeoModels` = **주문 축** → 이 필드를 보지 않는다.
   *   · `orderableSedeoModelsByTier` = **노출 축**(picker 목록·강등 착지) → 이 필드로 거른다.
   *     ★착지(`nearestOrderableSedeoModel`)가 노출 축을 쓰는 이유: 숨긴 티어로 조용히 낙착시키면
   *     유저는 고른 적 없는(그리고 화면에서 되돌릴 수도 없는) 모델로 과금된다.
   */
  readonly pickerHidden: boolean;
  /**
   * 🔢 **유저 노출 순서**(오름차순 = 싼 것부터). 표시 순서를 **카탈로그가 소유**한다 —
   * 소비처(video-model.ts)에 별도 목록을 두면 새 모델이 그 목록에 빠졌을 때 조용히 맨 앞으로
   * 정렬되고(Array.indexOf = -1), 그 목록과 카탈로그를 잇는 강제 캐스트가 필요해진다.
   */
  readonly tierRank: number;
}

/** 앱이 지원하는 해상도 전집합 — `src/lib/video-resolution.ts` 의 VIDEO_RESOLUTIONS 와 같아야 한다. */
const RES_ALL = ["480p", "720p", "1080p"] as const;
/** 저해상도 전용 모델(fast/mini 계열) — 벤더가 1080p 이상을 안 준다. */
const RES_LOW = ["480p", "720p"] as const;

export const SEDEO_MODELS = [
  {
    id: "seedance-2.0",
    arkModel: "dreamina-seedance-2-0-260128",
    label: "고급",
    hint: "고품질 · 컷 최대 15초 · 1080p 가능",
    maxDurationSec: 15,
    billingPrefix: "sedeo-2.0",
    // 벤더는 4K 도 지원하지만 앱 해상도 화이트리스트에 없다(4K = 별도 단가·RPM 15·동시성 1 → 별건).
    resolutions: RES_ALL,
    costMultiplier: 1.0,
    enabled: true,
    specsConfirmed: true,
    pickerHidden: false,
    tierRank: 3,
  },
  {
    id: "seedance-2.0-fast",
    arkModel: "dreamina-seedance-2-0-fast-260128",
    label: "일반",
    hint: "빠름 · 비용↓ · 컷 최대 12초",
    maxDurationSec: 12,
    billingPrefix: "sedeo-2.0-fast",
    // ⚠️fast 는 1080p 를 못 만든다 — ARK 직접 프로브 실측(2026-08-04, InvalidParameter
    //   "resolution not valid for dreamina-seedance-2-0-fast", task 미생성=무비용). ModelArk 문서와 일치.
    //   종전 RES_ALL 노출은 fast+1080p 선택 시 생성 실패를 만들었다. 저장분에 남은 fast+1080p 는
    //   품질쌍 원자 관문(applyVideoQualitySelection·PR#1552)이 720p 로 강등 고지하며 착지시킨다.
    //   단가행 sedeo-2.0-fast-1080p 는 죽은 행으로 잔류(도달 경로 0 — 제거는 선택).
    resolutions: RES_LOW,
    costMultiplier: 0.77,
    enabled: true,
    specsConfirmed: true,
    pickerHidden: false,
    tierRank: 2,
  },
  {
    id: "seedance-2.0-mini",
    arkModel: "dreamina-seedance-2-0-mini-260615",
    label: "저가",
    hint: "가성비 · 비용 최소 · 컷 최대 15초 · 720p까지",
    // ★fast 가 아니다 — mini 상한은 표준과 같은 15초(ModelArk 실측). 12 로 두면 대사가 잘린다.
    maxDurationSec: 15,
    billingPrefix: "sedeo-2.0-mini",
    resolutions: RES_LOW,
    // 벤더 토큰 단가 3.5 / 표준 7.0 = 정확히 0.5
    costMultiplier: 0.5,
    // 🚧한시 강하(2026-08-05 새벽): ARK 계정 3002392512 에서 mini 미활성(404 ModelNotOpen 실측)
    //   — 선택 시 제출이 반드시 실패한다(authorize 전이라 과금 무피해). Ark Console 활성화 확인 시
    //   이 플래그만 true 로 복원한다(복원 PR 1줄). 사유·트리거=이슈 #1539 계열·control-inbox 01:06 마커.
    enabled: false,
    specsConfirmed: true,
    pickerHidden: false,
    tierRank: 1,
  },
  {
    // ✅ Seedance 2.5 — **API 개방 확인**(2026-08-08~10 재검산). 종전 주석("API 가 아직 안 열렸다"·
    //   ModelArk 2026-08-01 "available soon")은 **낡았다**. 당시 관측한 404 ModelNotOpen 은 모델 미개방이
    //   아니라 **선불 리소스팩 미보유**였고, 구매 즉시 열렸다 — 1패스 30초·480p/720p 계약도 함께 확인해
    //   `specsConfirmed:true`·`maxDurationSec:30` 으로 확정한다.
    //   ★교훈: **막아둔 이유는 주기적으로 재검산한다** — "미개방"으로 적어 둔 플래그가 사유가 사라진 뒤에도
    //     몇 달간 그대로 남아 최상급 티어를 통째로 잠가 두고 있었다.
    //   ★1차 개방 범위는 **oneshot(⚡30초 한 방) 전용 라우팅**이다 — 아래 `pickerHidden:true` 참조.
    //     셀렉터 노출(유저가 직접 고르는 창구)은 단가 행·테넌트 게이트가 붙는 2차에서 연다.
    id: "seedance-2.5",
    arkModel: "dreamina-seedance-2-5-260628",
    label: "최상급",
    hint: "최고품질 · 컷 최대 30초 · 1080p 가능",
    // ★1패스 30초(벤더 실측) — ⚡30초 한 방 포맷의 길이 축(oneshotMaxSec)이 이 값에서 파생된다.
    maxDurationSec: 30,
    billingPrefix: "sedeo-2.5",
    resolutions: RES_ALL,
    costMultiplier: 1.53, // 10.70 / 7.0
    enabled: true,
    specsConfirmed: true,
    // 🙈 1차 = oneshot 전용 라우팅 개방 · 셀렉터 노출은 2차 테넌트 게이트에서.
    pickerHidden: true,
    tierRank: 4,
  },
] as const satisfies readonly SedeoModelDef[];

/**
 * 🪪 카탈로그가 아는 모델 id 전집합 — **문자열 리터럴 유니온의 정본**.
 * `satisfies`(값은 리터럴로 유지 + 형태는 SedeoModelDef 로 검증) 덕분에 소비처가 별도 목록을
 * 손으로 적지 않아도 되고, 새 모델을 카탈로그에 넣는 순간 타입에도 자동으로 들어온다.
 */
export type SedeoModelId = (typeof SEDEO_MODELS)[number]["id"];

/** 미지 모델이 올 때의 폴백 — **표준**. 종전 EF 동작(`fast 아니면 전부 표준`)과 정확히 같다. */
export const DEFAULT_SEDEO_MODEL_ID = "seedance-2.0";

/**
 * 🆔 **Seedance 2.5 모델 id** — 2.5 전용 확장(레퍼런스 캡·프롬프트 문법·테넌트 게이트)이 문자열
 * 리터럴 `"seedance-2.5"` 를 각자 손으로 적지 않게 카탈로그가 소유한다.
 * ★EF `_shared/sedeo-models.ts` 사본에도 **같은 값**이 있다(2벌 계약) — 한쪽만 고치지 말 것.
 */
export const SEDEO_25_MODEL_ID = "seedance-2.5";

// ★키 타입은 **넓은 string** 이다(리터럴 유니온이 아니다) — 이 맵의 존재 이유가 "카탈로그 밖
//   문자열이 들어왔을 때 폴백한다"라서, 키를 좁히면 옛 스냅샷·env 값 조회 자체가 타입 에러가 된다.
const BY_ID = new Map<string, (typeof SEDEO_MODELS)[number]>(SEDEO_MODELS.map((m) => [m.id, m]));

/**
 * 모델 정의 조회.
 * ★미지 문자열은 **표준으로 폴백**한다 — 레거시 호출부·옛 스냅샷이 종전과 똑같이 동작해야 하므로.
 * ★단 `enabled:false` 모델은 폴백 대상이 아니라 **그 정의를 그대로 돌려준다**. 호출부가
 *   `isOrderableSedeoModel` 로 걸러 400 을 내야 한다 — 조용히 표준으로 강등하면 그게 바로
 *   "싼 걸 골랐는데 비싼 게 나가는" 사고의 반대 방향(비싼 걸 골랐는데 다른 게 나감)이다.
 */
export function sedeoModelDef(id?: string | null): SedeoModelDef {
  const found = id ? BY_ID.get(id) : undefined;
  return found ?? BY_ID.get(DEFAULT_SEDEO_MODEL_ID)!;
}

/** 이 문자열이 카탈로그에 있는 시댄스 모델인가 — 폴링 라우팅·UI 분기의 판정 기준. */
export function isSedeoModel(id?: unknown): boolean {
  return typeof id === "string" && BY_ID.has(id);
}

/** 지금 주문 가능한가 — 화이트리스트 판정(미지 문자열도, 비활성 모델도 false). */
export function isOrderableSedeoModel(id?: unknown): boolean {
  return typeof id === "string" && (BY_ID.get(id)?.enabled ?? false);
}

/**
 * 주문 가능한 모델 목록 — **주문 축**(에이전트 API 화이트리스트·EF 거절 판정이 읽는다).
 * ★`pickerHidden` 을 보지 않는다 — 숨김은 "고르게 보여줄 것인가"의 문제이지 "주문 가능한가"가 아니다.
 *   유저 선택 UI 목록이 필요하면 `orderableSedeoModelsByTier()` 를 쓸 것.
 */
export function orderableSedeoModels(): readonly SedeoModelDef[] {
  return SEDEO_MODELS.filter((m) => m.enabled);
}

/**
 * 🔢 **유저에게 보여줄 순서**로 정렬한 주문 가능 모델 — `tierRank` 오름차순(싼 것부터).
 * ★정렬 기준이 카탈로그 안에 있으므로 새 모델을 추가해도 소비처를 고칠 일이 없다.
 *   (종전엔 video-model.ts 가 id 목록을 따로 들고 `indexOf` 로 정렬해, 목록에 없는 새 모델이
 *   -1 로 맨 앞에 서고 그 목록↔카탈로그를 잇는 강제 캐스트가 필요했다.)
 *
 * ★**노출 축**이다(2026-08-10) — `pickerHidden` 모델은 여기서 빠진다. 이 함수의 소비처가 곧
 *   "유저가 고르는 창구"의 전집합이기 때문이다: 품질 다이얼로그(SEEDANCE_MODELS)·캔버스 익스퍼트
 *   셀렉터·CinematicSetup VIDEO_MODELS, 그리고 강등 착지(`nearestOrderableSedeoModel`).
 *
 * 🪪 **`includeRouted` = 테넌트 인자화**(2026-08-10 · 2차 `sedeo_25` 게이트):
 *   true 면 `pickerHidden` 모델(=1차에 라우팅 전용으로만 연 2.5)까지 목록에 포함한다.
 *   ★`pickerHidden` 상수를 내리는 방식은 **쓰지 않는다**★ — 그러면 게이트 OFF 테넌트의 셀렉터에도
 *     최상급이 떠서 1차 계약("셀렉터 노출은 테넌트 게이트가 붙는 2차에서")이 통째로 깨진다.
 *   ★**기본값(미전달)은 종전 그대로 제외**★ — 모든 기존 호출부는 한 글자도 안 고쳐도 동작이 같다.
 *     특히 강등 착지(`nearestOrderableSedeoModel`)는 인자 없이 호출한다: 숨김/게이트 티어로 조용히
 *     낙착시키지 않는다는 규칙(⑤)은 게이트와 무관하게 불변이다.
 */
export function orderableSedeoModelsByTier(
  opts?: { includeRouted?: boolean },
): readonly (typeof SEDEO_MODELS)[number][] {
  const includeRouted = opts?.includeRouted === true;
  return SEDEO_MODELS
    .filter((m) => m.enabled && (!m.pickerHidden || includeRouted))
    .sort((a, b) => a.tierRank - b.tierRank);
}

/**
 * 🧭 **주문 불가 모델의 착지 지점** — 강하된 티어를 어디로 내릴지의 정본(카탈로그가 소유).
 *
 * 배경(#1714): `enabled:false` 로 내린 모델이 화이트리스트(`isOrderableSedeoModel`)에서 빠지면,
 *   저장분에 그 값을 들고 있던 작업물은 소비처의 `... : DEFAULT_SEEDANCE_MODEL` 가지로 떨어져
 *   **가장 비싼 티어로 승격**된다. 실측(2026-08-05 강하 후): 저가(mini)를 골라 둔 작업물이 표준으로
 *   낙착 — 초당 150→300(480p)·275→550(720p) = 정확히 **2배**(단가 근거=마이그레이션
 *   20260801020000_credit_costs_sedeo_mini.sql). 유저는 그 사실도, 되돌릴 수단도 갖지 못했다.
 *
 * 규칙:
 *   ① 주문 가능하면 그대로 — 착지가 필요 없다.
 *   ② 주문 불가면 **더 비싸지지 않는 쪽**에서 가장 가까운 주문 가능 티어(tierRank ≤ 원래 값 중 최고).
 *      ★유저가 '싸게'를 고른 의사를 자동 낙착이 뒤집지 않는다.
 *   ③ 그런 티어가 없으면(원래 값이 최저가였다) 주문 가능한 것 중 **가장 싼** 것 — 이 경우만 인상이며,
 *      호출부는 `describeVideoModelDemotion` 으로 **반드시 고지해야 한다**(clampResolutionToModel 과 같은 계약).
 *   ④ 카탈로그 밖 문자열은 `undefined` — 그건 유저가 고른 적 없는 값이라 '되돌릴 선택'이 없다.
 *      호출부가 종전대로 표준 기본값으로 떨어뜨린다(레거시 회귀 0).
 *   ⑤ **착지 후보는 노출 축**(`orderableSedeoModelsByTier`)이다 — `pickerHidden` 티어로는 낙착되지 않는다.
 *      숨긴 티어로 조용히 내려앉히면 유저는 고른 적도 없고 화면에서 되돌릴 수도 없는 모델로 과금된다.
 *      ★단 ①은 그대로다: **저장분이 그 숨긴 모델 자신**이면 그건 유저 선택이 아니라 라우팅 결과이고,
 *        바꾸는 순간이 곧 "고른 것과 다른 게 나가는" 사고다 — 그 정의를 그대로 돌려준다.
 *
 * ★EF(_shared) 사본에는 두지 않는다 — EF 는 주문 불가 모델을 **400 으로 거절**하는 것이 계약이고
 *   (조용한 강등 금지), 착지는 "유저에게 무엇을 보여주고 무엇으로 만들 것인가"라는 화면 결정이다.
 *   `sedeo-model-catalog.test.ts` 의 2벌 대조는 양쪽이 함께 내보내는 함수만 본다.
 */
export function nearestOrderableSedeoModel(id?: unknown): SedeoModelDef | undefined {
  if (typeof id !== "string") return undefined;
  const def = BY_ID.get(id);
  if (!def) return undefined; // 카탈로그 밖 = 유저 선택이 아니다
  if (def.enabled) return def;
  const orderable = orderableSedeoModelsByTier(); // 싼 것부터
  if (!orderable.length) return undefined; // 전부 내려간 상태 — 지어내지 않는다
  // 더 비싸지지 않는 쪽에서 가장 가까운 것. 없으면(원래가 최저가) 가장 싼 주문 가능 티어.
  return orderable.filter((m) => m.tierRank <= def.tierRank).at(-1) ?? orderable[0];
}

/**
 * 🗂 **저장분이 목록 밖일 때 셀렉터가 그릴 항목**(2026-08-10 적대 R10①).
 *
 * ★결함 형태★: `<select value={videoModel}>` 의 값이 옵션 목록에 없으면 브라우저는 ★빈 칸★ 을
 *   그린다. 게이트 OFF 테넌트(또는 배정 테넌트 열람)에서 2.5 로 저장해 둔 작업물을 열면 품질
 *   셀렉터가 **아무것도 안 고른 것처럼** 보인다 — 유저는 "설정이 날아갔다"고 읽고 다른 값을 눌러
 *   ★실제로★ 날린다(침묵이 만든 파괴).
 * ★강등하지 않는다★ — 저장분 보존은 이 레인의 확정 계약이다. 화면 ★표시★만 사실에 맞춘다.
 * `null` = 그릴 필요 없음(값이 목록에 있거나, 카탈로그 밖 문자열이라 되돌릴 '선택'이 아니다).
 */
export function outOfListSedeoModelOption(
  current: string | null | undefined,
  /** 이 화면의 셀렉터가 실제로 내는 값들(`seedanceModelsFor(...)`·`videoModelsFor(...)` 의 value). */
  offeredIds: readonly string[],
): { value: string; label: string } | null {
  const id = typeof current === "string" ? current.trim() : "";
  if (!id || offeredIds.includes(id)) return null;
  const def = BY_ID.get(id);
  if (!def) return null; // 카탈로그 밖 = 유저가 고른 적 없는 값(호출부의 기본값 폴백이 답이다)
  return { value: id, label: `${def.label} · ${def.hint} (저장된 설정 · 지금은 선택 불가)` };
}

/**
 * 🗂 목록 밖 저장분 한 줄 고지 — ★단가 숫자를 이 파일에 박지 않는다★.
 *   실단가는 `credit_costs` 행(테넌트 전용 > GLOBAL)이 정본이라, 숫자를 복제하면 행이 바뀌는 날
 *   화면만 옛 값을 말한다(#1601 §3 이 없애려던 그 거짓 고지). 대신 ★어느 티어로 과금되는지★ 를
 *   분명히 말하고 바꾸는 창구·비가역성을 함께 알린다(막다른 길 금지 + 조용한 승격 금지).
 *   `null` = 할 말 없음(위 함수와 같은 조건).
 */
export function outOfListSedeoModelNotice(
  current: string | null | undefined,
  offeredIds: readonly string[],
): string | null {
  const opt = outOfListSedeoModelOption(current, offeredIds);
  if (!opt) return null;
  const def = BY_ID.get(opt.value)!;
  return `이 작업물은 [${def.label}] 품질로 저장돼 있어요 — 이 워크스페이스 목록엔 없지만 설정은 그대로 유지되고, 생성도 그 품질(과 그 품질의 단가)로 나갑니다. 목록에서 다른 품질을 고르면 바뀌고, 그러면 여기로는 되돌릴 수 없어요.`;
}

/** 벤더(ARK) 모델 ID → 정의 역인덱스. env 로 모델을 직접 지정하는 경로(generate-story-cut 의
 *  `SEEDANCE_MODEL_ID`)가 과금 키를 **추측하지 않고** 카탈로그에서 끌어오게 한다.
 *  ★없으면 그 경로는 문자열 패턴(`/fast/` 유무)으로 2분기하는데, mini 의 ARK id
 *  (`dreamina-seedance-2-0-mini-260615`)는 그 패턴에 안 걸려 **표준가로 청구**된다. */
export function sedeoModelDefByArkModel(arkModel?: unknown): SedeoModelDef | undefined {
  if (typeof arkModel !== "string") return undefined;
  return SEDEO_MODELS.find((m) => m.arkModel === arkModel);
}

export function sedeoBillingModel(id: string | undefined, resolution: string): string {
  return `${sedeoModelDef(id).billingPrefix}-${resolution}`;
}

/** 이 모델이 그 해상도를 지원하는가. */
export function sedeoSupportsResolution(id: string | undefined, resolution: string): boolean {
  return sedeoModelDef(id).resolutions.includes(resolution);
}
